import React, { useEffect, useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import type { DevSettings } from '../../../utils/storage';

interface UsageBehaviorSettingsProps {
  devSettings: DevSettings;
  notificationThreshold: number;
  onDevSettingsChange: (newSettings: Partial<DevSettings>) => void;
  onResetDevSettings: () => void;
  onThresholdChange: (value: number) => void;
}

export const UsageBehaviorSettings: React.FC<UsageBehaviorSettingsProps> = ({
  devSettings,
  notificationThreshold,
  onDevSettingsChange,
  onResetDevSettings,
  onThresholdChange
}) => {
  const [thresholdDraft, setThresholdDraft] = useState(String(notificationThreshold));

  useEffect(() => {
    setThresholdDraft(String(notificationThreshold));
  }, [notificationThreshold]);

  const commitThreshold = () => {
    const value = Number(thresholdDraft);
    if (Number.isFinite(value) && value >= 0) {
      if (value !== notificationThreshold) onThresholdChange(value);
      setThresholdDraft(String(value));
    } else {
      setThresholdDraft(String(notificationThreshold));
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Usage Behavior</h3>

      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          API Key Limit for Usage Dashboard
        </label>
        <select
          value={devSettings.apiKeyLimit}
          onChange={(e) => onDevSettingsChange({
            apiKeyLimit: e.target.value === 'unlimited' ? 'unlimited' : parseInt(e.target.value)
          })}
          className="block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-white text-sm"
        >
          <option value="unlimited">Unlimited (All API Keys)</option>
          <option value="50">50 API Keys</option>
          <option value="100">100 API Keys</option>
          <option value="250">250 API Keys</option>
          <option value="500">500 API Keys</option>
        </select>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
          Use smaller limits to speed up loading on large organizations.
        </p>
      </div>

      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Daily User Cost Threshold ($, UTC)
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Alert when a user&apos;s combined OpenAI and Anthropic allocation is at or above this amount.
        </p>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
          <Input
            type="number"
            value={thresholdDraft}
            onChange={(event) => setThresholdDraft(event.target.value)}
            onBlur={commitThreshold}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
            min="0"
            step="0.5"
            className="pl-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            placeholder="9.5"
          />
        </div>
      </div>

      <Button onClick={onResetDevSettings} variant="outline" size="sm">
        Reset Application Settings
      </Button>
    </div>
  );
};
